import { useState, useRef, useEffect } from "react";
import { IoClose } from "react-icons/io5";

const TextDurationEditor = ({ 
    isVisible, 
    onClose, 
    elapsedTime,
    recordedVideoUrl,
    textStartTime,
    textEndTime,
    setTextStartTime,
    setTextEndTime
}) => {
    const [isDraggingStart, setIsDraggingStart] = useState(false);
    const [isDraggingEnd, setIsDraggingEnd] = useState(false);
    const timelineRef = useRef(null);
    const totalDuration = elapsedTime || 30; // Default to 30 seconds if no recording

    const handleMouseDown = (type) => {
        if (type === 'start') {
            setIsDraggingStart(true);
        } else {
            setIsDraggingEnd(true);
        }
    };

    const handleMouseMove = (e) => {
        if (!timelineRef.current || (!isDraggingStart && !isDraggingEnd)) return;

        const rect = timelineRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const newTime = percentage * totalDuration;

        if (isDraggingStart) {
            const maxStartTime = Math.max(0, textEndTime - 1); // At least 1 second before end
            setTextStartTime(Math.min(maxStartTime, newTime));
        } else if (isDraggingEnd) {
            const minEndTime = Math.min(totalDuration, textStartTime + 1); // At least 1 second after start
            setTextEndTime(Math.max(minEndTime, newTime));
        }
    };

    const handleMouseUp = () => {
        setIsDraggingStart(false);
        setIsDraggingEnd(false);
    };

    useEffect(() => {
        if (isDraggingStart || isDraggingEnd) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDraggingStart, isDraggingEnd, textStartTime, textEndTime, totalDuration]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getStartPosition = () => (textStartTime / totalDuration) * 100;
    const getEndPosition = () => (textEndTime / totalDuration) * 100;
    const getDurationWidth = () => getEndPosition() - getStartPosition();

    if (!isVisible) return null;

    return (
        <div className="absolute inset-0 bg-black/80 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 m-4 max-w-md w-full">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-black">Edit Text Duration</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <IoClose className="text-[24px]" />
                    </button>
                </div>

                {/* Recording Preview Scroller */}
                <div className="mb-4">
                    <p className="text-[12px] text-gray-600 mb-2">Video Timeline</p>
                    <div className="h-12 w-full overflow-x-auto bg-gray-100 rounded">
                        {recordedVideoUrl ? (
                            <div className="flex space-x-0 h-full" style={{ width: 'max-content', minWidth: '100%' }}>
                                {Array.from({ length: Math.ceil(totalDuration) }, (_, index) => (
                                    <div key={index} className="h-full w-8 bg-gray-300 border-r border-gray-400 overflow-hidden flex-shrink-0">
                                        <video
                                            muted
                                            src={recordedVideoUrl}
                                            disablePictureInPicture
                                            className="object-cover w-full h-full"
                                            onLoadedData={(e) => {
                                                e.target.currentTime = index;
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                                <p className="text-gray-600 text-xs">No recording available</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Timeline Controls */}
                <div className="mb-4">
                    <p className="text-[12px] text-gray-600 mb-2">Text Duration</p>
                    <div
                        ref={timelineRef}
                        className="relative h-8 bg-gray-200 rounded cursor-pointer"
                    >
                        {/* Duration Bar */}
                        <div
                            className="absolute h-full bg-[#38B6FF] rounded"
                            style={{
                                left: `${getStartPosition()}%`,
                                width: `${getDurationWidth()}%`
                            }}
                        />
                        
                        {/* Start Handle */}
                        <div
                            className="absolute top-0 w-3 h-8 bg-[#2da5ef] rounded cursor-ew-resize transform -translate-x-1/2 hover:bg-[#1e90ff]"
                            style={{ left: `${getStartPosition()}%` }}
                            onMouseDown={() => handleMouseDown('start')}
                        />
                        
                        {/* End Handle */}
                        <div
                            className="absolute top-0 w-3 h-8 bg-[#2da5ef] rounded cursor-ew-resize transform -translate-x-1/2 hover:bg-[#1e90ff]"
                            style={{ left: `${getEndPosition()}%` }}
                            onMouseDown={() => handleMouseDown('end')}
                        />
                    </div>
                    
                    {/* Time Display */}
                    <div className="flex justify-between mt-2 text-[12px] text-gray-600">
                        <span>Start: {formatTime(textStartTime)}</span>
                        <span>Duration: {formatTime(textEndTime - textStartTime)}</span>
                        <span>End: {formatTime(textEndTime)}</span>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2 px-4 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onClose}
                        className="flex-1 py-2 px-4 bg-[#38B6FF] text-white rounded hover:bg-[#2da5ef] transition-colors"
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TextDurationEditor;